import { useRef, useState, useEffect, useCallback } from "react";
import { getHeadersWithUniqueVisitorId } from "../../Auth/index.js";
import { retryConversation, stopConversation } from "../api.js";
function extractFollowUpsFromMessage(msg) {
  var _a, _b;
  if (!msg || msg.role !== "assistant") return [];
  const parts = msg.parts;
  if (!Array.isArray(parts)) return [];
  for (const p of parts) {
    if ((p == null ? void 0 : p.type) === "tool-result" && (p == null ? void 0 : p.toolName) === "suggest_follow_ups") {
      const accepted = (_a = p.result) == null ? void 0 : _a.accepted;
      if (Array.isArray(accepted)) {
        return accepted.filter((s) => typeof s === "string").slice(0, 3);
      }
    }
  }
  const followUpParts = parts.filter((p) => (p == null ? void 0 : p.type) === "data-follow-ups");
  if (followUpParts.length > 0) {
    const last = followUpParts[followUpParts.length - 1];
    const raw = ((_b = last == null ? void 0 : last.data) == null ? void 0 : _b.questions) ?? (last == null ? void 0 : last.questions);
    if (Array.isArray(raw)) {
      return raw.filter((s) => typeof s === "string").slice(0, 3);
    }
  }
  const meta = msg.metadata;
  if (meta && typeof meta === "object" && Array.isArray(meta.followUpSuggestions)) {
    return meta.followUpSuggestions.filter((s) => typeof s === "string");
  }
  return [];
}
function generateMessageId() {
  return `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}
function useConciergeChat(options) {
  const {
    apiBaseUrl,
    conversationId,
    sharedConversationId,
    appConfigId,
    devOptions,
    pdpStacklineSku,
    isOnChatWidget,
    onError,
    onTripwire,
    onConversationIdGenerated
  } = options;
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;
  const sharedConversationIdRef = useRef(sharedConversationId);
  sharedConversationIdRef.current = sharedConversationId;
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState("ready");
  const [transientStatus, setTransientStatus] = useState(null);
  const [tripwireMessage, setTripwireMessage] = useState(null);
  const [latestFollowUps, setLatestFollowUps] = useState([]);
  const [isLoadingFollowUps, setIsLoadingFollowUps] = useState(false);
  const lastFetchedForMessageIdRef = useRef(null);
  const abortControllerRef = useRef(null);
  useEffect(() => {
    if (status !== "streaming") setTransientStatus(null);
  }, [status]);
  useEffect(() => {
    setLatestFollowUps([]);
    setIsLoadingFollowUps(false);
    lastFetchedForMessageIdRef.current = null;
  }, [conversationId]);
  useEffect(() => {
    if (status !== "ready") return;
    const lastAssistantMsg = [...messages].reverse().find((m) => m.role === "assistant");
    if (!(lastAssistantMsg == null ? void 0 : lastAssistantMsg.id)) return;
    if (lastAssistantMsg.id === lastFetchedForMessageIdRef.current) return;
    lastFetchedForMessageIdRef.current = lastAssistantMsg.id;
    setLatestFollowUps(extractFollowUpsFromMessage(lastAssistantMsg));
    setIsLoadingFollowUps(false);
  }, [status, messages]);
  const stop = useCallback(() => {
    var _a;
    (_a = abortControllerRef.current) == null ? void 0 : _a.abort();
    abortControllerRef.current = null;
    setStatus("ready");
  }, []);
  const processSSEStream = useCallback(
    async (response) => {
      var _a;
      const reader = (_a = response.body) == null ? void 0 : _a.getReader();
      if (!reader) return;
      const decoder = new TextDecoder();
      let buffer = "";
      const assistantId = generateMessageId();
      const parts = [];
      const currentTextParts = /* @__PURE__ */ new Map();
      const updateAssistantMessage = () => {
        const collectedParts = [...parts];
        currentTextParts.forEach((text) => {
          if (text) collectedParts.push({ type: "text", text });
        });
        setMessages((prev) => {
          const existing = prev.findIndex((m) => m.id === assistantId);
          const msg = { id: assistantId, role: "assistant", parts: collectedParts };
          if (existing >= 0) {
            const updated = [...prev];
            updated[existing] = msg;
            return updated;
          }
          return [...prev, msg];
        });
      };
      const handleSSEEvent = (event, data) => {
        switch (event) {
          case "text-start":
            currentTextParts.set(data.id, "");
            break;
          case "text-delta":
            currentTextParts.set(data.id, (currentTextParts.get(data.id) || "") + (data.delta || ""));
            updateAssistantMessage();
            break;
          case "text-end": {
            const text = currentTextParts.get(data.id) || "";
            currentTextParts.delete(data.id);
            if (text) parts.push({ type: "text", text });
            updateAssistantMessage();
            break;
          }
          case "tool-call":
            parts.push({
              type: "tool-call",
              toolCallId: data.toolCallId,
              toolName: data.toolName,
              args: data.args
            });
            updateAssistantMessage();
            break;
          case "tool-result":
            parts.push({
              type: "tool-result",
              toolCallId: data.toolCallId,
              toolName: data.toolName,
              result: data.result
            });
            updateAssistantMessage();
            break;
          case "tripwire": {
            const reason = data.reason || data.message;
            if (reason) {
              setTripwireMessage(reason);
              onTripwire == null ? void 0 : onTripwire(reason);
            }
            break;
          }
          case "finish":
            break;
          default:
            if (event.startsWith("data-")) {
              if (event === "data-conversation-id") {
                const serverConversationId = data == null ? void 0 : data.conversationId;
                if (serverConversationId && serverConversationId !== conversationIdRef.current) {
                  conversationIdRef.current = serverConversationId;
                  onConversationIdGenerated == null ? void 0 : onConversationIdGenerated(serverConversationId);
                }
              } else if (event === "data-status") {
                setTransientStatus((data == null ? void 0 : data.status) ?? null);
              } else if (event === "data-tripwire") {
                const message = data == null ? void 0 : data.message;
                if (message) {
                  setTripwireMessage(message);
                  onTripwire == null ? void 0 : onTripwire(message);
                }
              } else {
                parts.push({ type: event, data });
                updateAssistantMessage();
              }
            }
            break;
        }
      };
      try {
        let streamDone = false;
        while (!streamDone) {
          const { done, value } = await reader.read();
          if (done) {
            streamDone = true;
            break;
          }
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";
          let currentEvent = "";
          let currentData = "";
          for (const line of lines) {
            if (line.startsWith("event: ")) {
              currentEvent = line.slice(7).trim();
            } else if (line.startsWith("data: ")) {
              currentData = line.slice(6);
            } else if (line === "" && currentEvent && currentData) {
              try {
                const parsed = JSON.parse(currentData);
                handleSSEEvent(currentEvent, parsed);
              } catch {
              }
              currentEvent = "";
              currentData = "";
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    },
    [onConversationIdGenerated, onTripwire]
  );
  const sendMessage = useCallback(
    async (prompt) => {
      const text = typeof prompt === "string" ? prompt : prompt.text;
      const files = typeof prompt === "string" ? void 0 : prompt.files;
      if (!text || text.trim().length === 0) return;
      setTripwireMessage(null);
      setLatestFollowUps([]);
      setIsLoadingFollowUps(false);
      const userParts = [{ type: "text", text }];
      if (files == null ? void 0 : files.length) {
        for (const f of files) {
          userParts.push({ type: "file", mediaType: f.mediaType, url: f.url });
        }
      }
      const userMessage = { id: generateMessageId(), role: "user", parts: userParts };
      setMessages((prev) => [...prev, userMessage]);
      setStatus("submitted");
      const controller = new AbortController();
      abortControllerRef.current = controller;
      try {
        const headers = await getHeadersWithUniqueVisitorId();
        const res = await fetch(`${apiBaseUrl}/concierge/agent/stream`, {
          method: "POST",
          headers: {
            ...headers,
            "Content-Type": "application/json",
            Accept: "text/event-stream"
          },
          body: JSON.stringify({
            prompt: text,
            conversationId: conversationIdRef.current,
            sharedConversationId: sharedConversationIdRef.current,
            appConfigId,
            devOptions,
            ...pdpStacklineSku ? { pdpStacklineSku } : {},
            ...isOnChatWidget ? { isOnChatWidget: true } : {},
            ...(files == null ? void 0 : files.length) ? { fileParts: files } : {}
          }),
          signal: controller.signal
        });
        if (!res.ok) {
          throw new Error(`Stream request failed: ${res.status} ${res.statusText}`);
        }
        setStatus("streaming");
        await processSSEStream(res);
      } catch (err) {
        if ((err == null ? void 0 : err.name) === "AbortError") return;
        console.error("[useConciergeChat] Stream error:", err);
        onError == null ? void 0 : onError(err instanceof Error ? err : new Error(String(err)));
        setStatus("error");
        return;
      } finally {
        abortControllerRef.current = null;
        setStatus((prev) => prev === "error" ? prev : "ready");
      }
    },
    [apiBaseUrl, appConfigId, devOptions, pdpStacklineSku, isOnChatWidget, onError, processSSEStream]
  );
  const trimLocalMessagesFromLastUser = useCallback(() => {
    setMessages((prev) => {
      const lastUserIdx = prev.map((m) => m.role).lastIndexOf("user");
      if (lastUserIdx === -1) return prev;
      return prev.slice(0, lastUserIdx);
    });
  }, []);
  const getLastUserPromptFromMessages = useCallback(() => {
    const lastUserMessage = [...messages].reverse().find((m) => m.role === "user");
    if (!lastUserMessage) return "";
    return (lastUserMessage.parts || []).filter((p) => p.type === "text" && typeof p.text === "string").map((p) => p.text).join("").trim();
  }, [messages]);
  const retryLastMessage = useCallback(async () => {
    const convId = conversationIdRef.current;
    if (!convId) return;
    try {
      const headers = await getHeadersWithUniqueVisitorId();
      const { prompt } = await retryConversation({
        conversationId: convId,
        headersToInject: headers,
        apiBaseUrl
      });
      setMessages((prev) => {
        const lastAssistantIdx = prev.map((m) => m.role).lastIndexOf("assistant");
        if (lastAssistantIdx === -1) return prev;
        const lastUserIdx = prev.slice(0, lastAssistantIdx).map((m) => m.role).lastIndexOf("user");
        const cutFrom = lastUserIdx !== -1 ? lastUserIdx : lastAssistantIdx;
        return prev.slice(0, cutFrom);
      });
      setTripwireMessage(null);
      await sendMessage(prompt);
    } catch (error) {
      console.error("[useConciergeChat] retryLastMessage failed:", error);
      onError == null ? void 0 : onError(error instanceof Error ? error : new Error(String(error)));
    }
  }, [apiBaseUrl, onError, sendMessage]);
  const cancelLatestTurn = useCallback(async () => {
    stop();
    const fallbackPrompt = getLastUserPromptFromMessages();
    const convId = conversationIdRef.current;
    if (!convId) {
      trimLocalMessagesFromLastUser();
      setTripwireMessage(null);
      return fallbackPrompt || null;
    }
    try {
      const headers = await getHeadersWithUniqueVisitorId();
      const { prompt } = await stopConversation({
        conversationId: convId,
        headersToInject: headers,
        apiBaseUrl
      });
      trimLocalMessagesFromLastUser();
      setTripwireMessage(null);
      return (prompt || fallbackPrompt || "").trim() || null;
    } catch (error) {
      console.error("[useConciergeChat] cancelLatestTurn failed:", error);
      onError == null ? void 0 : onError(error instanceof Error ? error : new Error(String(error)));
      return null;
    }
  }, [apiBaseUrl, getLastUserPromptFromMessages, onError, stop, trimLocalMessagesFromLastUser]);
  const isStreaming = status === "streaming";
  const isLoading = status === "submitted";
  return {
    messages,
    setMessages,
    status,
    stop,
    sendMessage,
    retryLastMessage,
    cancelLatestTurn,
    isStreaming,
    isLoading,
    isWaiting: isLoading || isStreaming,
    transientStatus,
    tripwireMessage,
    latestFollowUps,
    isLoadingFollowUps
  };
}
export {
  useConciergeChat
};
//# sourceMappingURL=useConciergeChat.js.map
